import React from 'react';
import LegalPage from '../../components/LegalPage/LegalPage';

const sections = [
   {
      heading: 'Introduction',
      paragraphs: [
         'Welcome to Harsoft Solutions Private Limited ("Harsoft", "we", "our", or "us").',
         'These Terms and Conditions govern your access to and use of our website, https://harsofts.com/, and the information, services, content, and business communications made available through it.',
         'By accessing or using our website, you agree to these Terms and Conditions. If you do not agree with these terms, please discontinue use of our website.',
      ],
   },
   {
      heading: 'About Harsoft',
      paragraphs: [
         'Harsoft Solutions Private Limited is a technology and software solutions company providing technology-related services and business solutions.',
         'Information about our services, capabilities, projects, and offerings may be published on our website and updated periodically.',
         'Specific services, project deliverables, fees, timelines, and contractual obligations may be governed by separate proposals, statements of work, or agreements entered into with our clients.',
      ],
   },
   {
      heading: 'Eligibility and Acceptable Use',
      content: [
         'You agree to use our website only for lawful purposes and in accordance with these Terms and Conditions.',
         'You must not:',
         {
            list: [
               'Use the website in violation of applicable laws or regulations.',
               'Attempt to gain unauthorised access to our website, systems, servers, or databases.',
               'Introduce malicious software, viruses, or other harmful code.',
               'Interfere with the operation, security, or availability of the website.',
               'Use automated tools to extract website content in a manner that violates applicable law or our rights.',
               'Copy, reproduce, distribute, or exploit our website content without appropriate authorisation.',
               'Submit false, misleading, fraudulent, or unlawful information through our forms or communications.',
            ],
         },
         'We reserve the right to restrict access to the website where reasonably necessary to protect our systems, users, or legal rights.',
      ],
   },
   {
      heading: 'Website Information and Accuracy',
      paragraphs: [
         'We make reasonable efforts to keep the information on our website accurate and up to date.',
         'However, website content is provided for general informational purposes. We do not guarantee that all information will always be complete, accurate, current, or available without interruption.',
         'Service descriptions, features, project examples, and other information may change without prior notice.',
         'Information published on the website does not constitute a binding offer, quotation, or commitment unless expressly confirmed by Harsoft in writing.',
      ],
   },
   {
      heading: 'Services and Business Enquiries',
      paragraphs: [
         'You may use our website to learn about our services or submit business enquiries.',
         'Submitting an enquiry, requesting a quotation, or receiving an initial response does not automatically create a client relationship or contractual obligation.',
         'Any engagement for software development, consulting, digital marketing, technology support, or other services will be subject to mutually agreed commercial terms, scope of work, deliverables, payment terms, timelines, and any applicable service agreement.',
         'Where a separate agreement conflicts with these Terms and Conditions, the separate agreement will govern the relevant engagement to the extent of the conflict.',
      ],
   },
   {
      heading: 'Intellectual Property Rights',
      content: [
         'Unless otherwise indicated, the content on this website, including text, graphics, logos, illustrations, designs, layouts, images, software, and other materials, is owned by Harsoft or used with appropriate permission.',
         'You may access and view website content for personal, informational, or legitimate business evaluation purposes.',
         'Unless authorised by us in writing or permitted by applicable law, you must not:',
         {
            list: [
               'Reproduce or republish website content.',
               'Modify or create derivative works from our protected materials.',
               'Distribute, sell, license, or commercially exploit our content.',
               'Use our name, logo, branding, or other protected assets in a way that suggests an unauthorised association or endorsement.',
            ],
         },
         'All third-party trademarks, logos, and brand names remain the property of their respective owners.',
         'Ownership and licensing of software, designs, source code, and other deliverables created for a client will be governed by the applicable client agreement.',
      ],
   },
   {
      heading: 'Enquiries and Information Submitted by You',
      paragraphs: [
         'When submitting information through our website or communicating with us, you agree to provide information that is accurate to the best of your knowledge and that you are authorised to share.',
         "You must not submit information that infringes another person's rights, violates applicable law, or contains malicious code.",
         'We may use the information you provide to respond to your enquiries, prepare proposals, communicate with you, and provide requested business information in accordance with our Privacy Policy.',
         'Please do not submit confidential, sensitive, or proprietary information through a general website form unless the relevant submission process is intended for that purpose.',
      ],
   },
   {
      heading: 'Marketing and Communications',
      paragraphs: [
         'If you contact us, request information, or submit an enquiry, we may communicate with you regarding your request or our relevant services.',
         'We may send promotional messages or marketing communications where permitted by applicable law and, where required, with your consent.',
         'You may opt out of promotional communications at any time using the available unsubscribe mechanism or by contacting us.',
         'Opting out of marketing communications does not prevent us from sending necessary service-related, security, or transactional messages.',
      ],
   },
   {
      heading: 'Third-Party Links and Services',
      paragraphs: [
         'Our website may contain links to third-party websites, applications, platforms, or services.',
         'These links are provided for convenience or informational purposes. We do not control all third-party websites and are not responsible for their content, availability, security, or independent business practices.',
         'Your use of third-party services may be subject to separate terms, privacy policies, and other conditions established by their respective providers.',
         'You should review those terms before using external services or sharing information with them.',
      ],
   },
   {
      heading: 'Privacy and Data Protection',
      paragraphs: [
         'Your use of our website is also subject to our Privacy Policy: https://harsofts.com/privacy-policy',
         'Our Privacy Policy explains how we collect, use, store, and protect personal information and how you may exercise applicable privacy rights.',
         'By using our website, you acknowledge that you should review the Privacy Policy before submitting personal information. Any consent required by applicable law will be obtained separately where necessary.',
      ],
   },
   {
      heading: 'Website Availability and Security',
      paragraphs: [
         'We aim to maintain the availability and security of our website. However, we do not guarantee uninterrupted, error-free, or continuously secure access.',
         'The website may occasionally be unavailable because of maintenance, technical issues, infrastructure failures, security incidents, or circumstances beyond our reasonable control.',
         'We may modify, suspend, or discontinue any part of the website when reasonably necessary.',
      ],
   },
   {
      heading: 'Disclaimer of Warranties',
      paragraphs: [
         'To the extent permitted by applicable law, the website and its general informational content are provided on an "as is" and "as available" basis.',
         'We do not warrant that the website will always be uninterrupted, error-free, compatible with every device, or free from harmful components.',
         'Nothing in these Terms and Conditions excludes any warranty, right, or protection that cannot lawfully be excluded under applicable law.',
      ],
   },
   {
      heading: 'Limitation of Liability',
      paragraphs: [
         'To the extent permitted by applicable law, Harsoft will not be liable for indirect, incidental, special, or consequential losses arising from your use of, or inability to use, the website, including losses resulting from temporary unavailability, reliance on general website information, or third-party website content.',
         'This section does not exclude or limit liability where such exclusion or limitation is prohibited by law, including any applicable consumer rights or liability that cannot lawfully be restricted.',
         'Any liability arising from a separately contracted service will be governed by the applicable agreement and applicable law.',
      ],
   },
   {
      heading: 'Indemnity',
      paragraphs: [
         'To the extent permitted by applicable law, you agree to be responsible for losses or claims arising from your unlawful use of the website, your material violation of these Terms and Conditions, or your infringement of third-party rights.',
         'This provision does not apply to the extent a claim results from our own unlawful conduct or where such allocation of responsibility is prohibited by law.',
      ],
   },
   {
      heading: 'Suspension and Termination',
      paragraphs: [
         'We may restrict or suspend access to our website where reasonably necessary because of security concerns, suspected misuse, unlawful activity, or a material violation of these terms.',
         'Suspension of website access does not automatically terminate any separate client agreement. Such agreements will be governed by their own terms.',
      ],
   },
   {
      heading: 'Changes to These Terms',
      paragraphs: [
         'We may revise these Terms and Conditions from time to time to reflect changes in our business, website functionality, services, or applicable legal requirements.',
         'Updated terms will be published on this page with a revised "Last Updated" date.',
         'Your continued use of the website after changes are published constitutes acceptance of the revised terms only to the extent recognised under applicable law. Where affirmative consent or notice is legally required, we will follow the applicable requirements.',
      ],
   },
   {
      heading: 'Governing Law and Jurisdiction',
      paragraphs: [
         'These Terms and Conditions shall be governed by the laws of India.',
         'Subject to applicable law, disputes arising from the use of this website shall be submitted to the competent courts having jurisdiction over the matter and the relevant parties.',
         'Nothing in this section limits any mandatory rights or remedies available under applicable law.',
      ],
   },
];

const TermsAndConditions = () => (
   <LegalPage
      title="Terms and Conditions"
      description="Read the terms and conditions governing your use of the Harsoft Solutions Private Limited website and services."
      path="/terms-and-conditions"
      effective="9 October 2026"
      updated="9 October 2026"
      company="Harsoft Solutions Private Limited"
      intro={[]}
      sections={sections}
      note="These Terms and Conditions provide general information about the use of our website and do not constitute legal advice."
   />
);

export default TermsAndConditions;
